import React, { useEffect } from 'react';
import {
	Box,
	Button,
	Drawer,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { ICountryFE } from 'features/common/types';
import { nanoidGenerator } from 'features/common/helpers';
import { getCountries } from 'features/common/services';
import { IPatchProviderForm, IProviderDTO } from '../../types';
import { getProviderById, putProvider } from '../../services';

interface ProviderEditDrawerProps {
	open: boolean;
	providerId?: string;
	onClose: () => void;
	onSaved: () => void;
}

export const ProviderEditDrawer = ({ open, providerId, onClose, onSaved }: ProviderEditDrawerProps): JSX.Element => {
	const isEdit = !!providerId;
	const [countries, setCountries] = React.useState<ICountryFE[]>([]);

	const {
		register,
		reset,
		handleSubmit,
		setValue,
		control,
		formState: { errors },
	} = useForm<IPatchProviderForm>();

	useEffect(() => {
		if (!open) return;

		const fetchCountries = async () => {
			try {
				const { data } = await getCountries();
				setCountries(data);
			} catch (_e) {
				console.error(_e);
			}
		};

		fetchCountries();
	}, [open]);

	useEffect(() => {
		if (!open) return;

		const loadProvider = async () => {
			if (providerId) {
				const { data } = await getProviderById(providerId);
				setValue('id', data.id);
				setValue('fantasyName', data.fantasyName);
				setValue('businessName', data.businessName);
				setValue('country', data.country.code);
				setValue('stateName', data.stateName);
				setValue('cityName', data.cityName);
				setValue('street', data.street);
				setValue('postalCode', data.postalCode);
				setValue('rut', data.rut);
				setValue('phone', data.phone);
				setValue('email', data.email);
				setValue('enable', data.enable ? 'true' : 'false');
			} else {
				reset();
				setValue('id', nanoidGenerator());
			}
		};

		loadProvider();
	}, [open, providerId, reset, setValue]);

	const onSubmit = async (formData: IPatchProviderForm) => {
		const countryFound = countries.find((item) => item.code === formData.country);
		if (!countryFound) return;

		const request: Partial<IProviderDTO> = {
			id: formData.id,
			fantasyName: formData.fantasyName,
			businessName: formData.businessName,
			country: {
				code: countryFound.code,
				name: countryFound.name,
			},
			stateName: formData.stateName,
			cityName: formData.cityName,
			enable: formData.enable === 'true',
		};

		if (formData.street) request.street = formData.street;
		if (formData.postalCode) request.postalCode = formData.postalCode;
		if (formData.rut) request.rut = formData.rut;
		if (formData.phone) request.phone = formData.phone;
		if (formData.email) request.email = formData.email;

		await putProvider(request);
		onSaved();
		onClose();
	};

	return (
		<Drawer anchor="right" open={open} onClose={onClose}>
			<Box sx={{ padding: 3, maxWidth: 600 }} display="flex" flexDirection="column" gap={3}>
				<Typography component="h1" variant="h5">
					{isEdit ? 'Editar proveedor' : 'Nuevo proveedor'}
				</Typography>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<input type="hidden" {...register('id')} />
							<TextField
								variant="outlined"
								required
								fullWidth
								label="Razón social"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('businessName', { required: true })}
								error={!!errors.businessName}
								helperText={errors.businessName && 'Campo requerido'}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								required
								fullWidth
								label="Nombre fantasía"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('fantasyName', { required: true })}
								error={!!errors.fantasyName}
								helperText={errors.fantasyName && 'Campo requerido'}
							/>
						</Grid>
						<Grid item xs={12}>
							<FormControl size="small" fullWidth>
								<InputLabel id="country">País</InputLabel>
								<Controller
									render={({ field }) => (
										<Select labelId="country" label="País" {...field}>
											{countries.map((item) => (
												<MenuItem key={item.code} value={item.code}>
													{item.name}
												</MenuItem>
											))}
										</Select>
									)}
									defaultValue="UY"
									name="country"
									control={control}
								/>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								required
								fullWidth
								label="Estado"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('stateName', { required: true })}
								error={!!errors.stateName}
								helperText={errors.stateName && 'Campo requerido'}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								required
								fullWidth
								label="Ciudad"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('cityName', { required: true })}
								error={!!errors.cityName}
								helperText={errors.cityName && 'Campo requerido'}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								fullWidth
								label="Dirección"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('street')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								fullWidth
								label="Código postal"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('postalCode')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								fullWidth
								label="RUT"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('rut')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								fullWidth
								label="Teléfono"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('phone')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								fullWidth
								label="Email"
								size="small"
								InputLabelProps={{ shrink: true }}
								{...register('email')}
							/>
						</Grid>
						<Grid item xs={12}>
							<FormControl size="small" fullWidth>
								<InputLabel id="status">Estado</InputLabel>
								<Controller
									render={({ field }) => (
										<Select labelId="status" label="Estado" defaultValue="true" {...field}>
											<MenuItem value="true">Activo</MenuItem>
											<MenuItem value="false">Inactivo</MenuItem>
										</Select>
									)}
									defaultValue="true"
									name="enable"
									control={control}
								/>
							</FormControl>
						</Grid>
					</Grid>
					<Box display="flex" flexDirection="row-reverse" mt={2}>
						<Button variant="contained" type="submit">
							Guardar
						</Button>
					</Box>
				</form>
			</Box>
		</Drawer>
	);
};
